import React, { useState } from 'react';
import arrow from '../assets/images/icon/arrow_forward_ios.png';
import orangearrow from '../assets/images/icon/orange-down.png';
import dashboard from '../assets/images/icon/dashboard.png';
import employees from '../assets/images/icon/employees.png';
import contractor from '../assets/images/icon/contractor.png';
import benefits from '../assets/images/icon/benefits.png';
import deductions from '../assets/images/icon/deductions.png';
import reports from '../assets/images/icon/reports.png';
import payrollnootIcon from '../assets/images/payrollnoot-icon.png';
import '../styles/main.scss'
import { NavLink } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import { cn, stringMerger } from 'utils';
import useApp from 'hooks/useApp';
import useFeatures from 'hooks/useFeatures';
import useBranding from 'hooks/useBranding';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../axios/axiosInstance';
import { listOffboarding } from 'services/offboarding';
import { mustSelectCompany, getLinkedCompanies } from 'utils/companyContext';

const menuItems = [
  {
    name: 'Dashboard',
    icon: dashboard,
    featureKey: 'dashboard',
    path: routes.DASHBOARD.HOME,
    otherPaths: [
      stringMerger(routes.DASHBOARD.HOME, routes.DASHBOARD.PAYROLL_TIME_KEEPING.HOME),
      stringMerger(routes.DASHBOARD.HOME, routes.DASHBOARD.PAYROLL_TIME_KEEPING.HOME, routes.DASHBOARD.PAYROLL_TIME_KEEPING.ADD_TIME_CARD),
      stringMerger(routes.DASHBOARD.HOME, routes.DASHBOARD.MANAGE_COMPANY),
      stringMerger(routes.DASHBOARD.HOME, routes.DASHBOARD.START_NEW_PAYROLL),
    ]
  },
  {
    name: 'Employees',
    icon: employees,
    featureKey: 'manage_employees',
    path: routes.EMPLOYEES.HOME,
    otherPaths: [
      stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.ADD_EDIT_EMPLOYEE),
      stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.VIEW),
      stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.OFFBOARDING),
    ]
  },
  {
    name: 'Offboarding',
    icon: employees,
    featureKey: 'manage_employees',
    path: stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.OFFBOARDING),
    otherPaths: [],
    showOffboardingBadge: true,
  },
  {
    name: 'Contractor',
    icon: contractor,
    featureKey: 'manage_contractors',
    path: routes.CONTRACTOR.HOME,
    otherPaths: [
      stringMerger(routes.CONTRACTOR.HOME, routes.CONTRACTOR.VIEW),
      stringMerger(routes.CONTRACTOR.HOME, routes.CONTRACTOR.ADD_EDIT_CONTRACTOR),
    ]
  },
  {
    name: 'Benefits',
    icon: benefits,
    featureKey: 'benefits',
    path: routes.BENEFITS.HOME,
    otherPaths: [
      stringMerger(routes.BENEFITS.HOME, routes.BENEFITS.ADD_EDIT_COMPANY_BENEFIT),
      stringMerger(routes.BENEFITS.HOME, routes.BENEFITS.ADD_EDIT_EMPLOYEE_BENEFIT),
    ]
  },
  {
    name: 'Deductions',
    icon: deductions,
    featureKey: 'deductions',
    path: routes.DEDUCTIONS.HOME,
    otherPaths: [
      stringMerger(routes.DEDUCTIONS.HOME, routes.DEDUCTIONS.ADD_EDIT_MCN_DEDUCTION),
      stringMerger(routes.DEDUCTIONS.HOME, routes.DEDUCTIONS.ADD_EDIT_CHILD_SUPPORT_DEDUCTION),
    ]
  },
  {
    name: 'Time Off',
    icon: benefits,
    featureKey: 'time_off',
    path: routes.TIME_OFF.HOME,
    otherPaths: [],
  },
  {
    name: 'Reports',
    icon: reports,
    featureKey: 'reports',
    path: stringMerger(routes.REPORTS.HOME, routes.REPORTS.ALL_REPORTS),
    otherPaths: [
      stringMerger(routes.REPORTS.HOME),
      stringMerger(routes.REPORTS.HOME, routes.REPORTS.AUTHORIZATION_DOCS),
      stringMerger(routes.REPORTS.HOME, routes.REPORTS.COMPANY_TAX_DOCS),
      stringMerger(routes.REPORTS.HOME, routes.REPORTS.CONTRACTOR_TAX_DOCS),
      stringMerger(routes.REPORTS.HOME, routes.REPORTS.EMPLOYEE_TAX_DOCS),
      stringMerger(routes.REPORTS.HOME, routes.REPORTS.AUDIT_TRAIL),
    ]
  },
  {
    name: 'My Companies',
    icon: employees,
    featureKey: 'multi_company',
    path: '/dashboard/my-companies',
    otherPaths: ['/dashboard/firm-staff'],
    multiCompanyOnly: true,
    companyOptional: true, // firm-level — usable with no client selected
  },
  {
    name: 'Firm Staff',
    icon: employees,
    featureKey: 'multi_company',
    path: '/dashboard/firm-staff',
    otherPaths: [],
    multiCompanyOnly: true,
    companyOptional: true,
  },
  {
    name: 'Locations',
    icon: employees,
    featureKey: 'location_management',
    path: '/dashboard/locations',
    otherPaths: [],
  },
  {
    name: 'Billing',
    icon: reports,
    featureKey: 'billing',
    path: '/dashboard/billing',
    otherPaths: [],
    companyOptional: true,
  },
  {
    name: 'Onboarding',
    icon: employees,
    featureKey: 'user_management',
    path: routes.ONBOARDING.HOME,
    otherPaths: [],
    showBadge: true,
  },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isSidebarOpen } = useApp();
  const { hasFeature } = useFeatures();
  const branding = useBranding();

  const companyId = localStorage.getItem('companyId');
  const userRole = localStorage.getItem('userRole');
  const needsCompany = mustSelectCompany();
  const linkedCount = getLinkedCompanies().length;
  const canManagePortfolio = userRole === 'Accountant' || userRole === 'Reseller';
  const [lockModal, setLockModal] = useState(false);

  const { data: pendingAccounts = [] } = useQuery({
    queryKey: ['pending-approvals', companyId],
    queryFn: async () => (await axiosInstance.get(`/auth/pending-approvals/${companyId}`)).data ?? [],
    enabled: !!companyId,
    refetchInterval: 30000,
  });
  const { data: offboardingData = [] } = useQuery({
    queryKey: ['offboarding', companyId],
    queryFn: () => listOffboarding(),
    enabled: !!companyId && hasFeature('manage_employees'),
    refetchInterval: 60000,
  });

  const pendingCount = pendingAccounts.length;
  const offboardingOpenCount = (() => {
    const list = Array.isArray(offboardingData) ? offboardingData : (offboardingData?.items || []);
    return list.filter(r => {
      const status = (r.status || r.Status || '').toLowerCase();
      if (!['scheduled', 'applied'].includes(status)) return false;
      if (status === 'scheduled') return true;
      const tasks = r.tasks || r.Tasks || [];
      return tasks.some(t => !(t.isDone ?? t.IsDone));
    }).length;
  })();
  const visibleItems = menuItems.filter(item => {
    if (item.multiCompanyOnly && userRole !== 'Accountant' && userRole !== 'Reseller') return false;
    return hasFeature(item.featureKey);
  });

  const customLogo = branding.logoUrl;
  const productName = branding.productName || 'PayrollNoot';
  const [logoFailed, setLogoFailed] = useState(false);

  React.useEffect(() => {
    setLogoFailed(false);
  }, [customLogo]);

  const handleNav = (item) => {
    if (needsCompany && !item.companyOptional) {
      setLockModal(true);
      return;
    }
    navigate(item.path);
  };

  const goMyCompanies = (tab) => {
    setLockModal(false);
    navigate(tab ? `/dashboard/my-companies?tab=${tab}` : '/dashboard/my-companies');
  };

  return (
    <div className={cn("sidebar", isSidebarOpen ? "open" : "closed")}>
      <div className="sidebar-logo-container">
        {customLogo && !logoFailed ? (
          <img
            src={customLogo}
            alt={productName}
            className="sidebar-logo"
            style={{ height: 40, maxHeight: 40, maxWidth: 200, width: 'auto', objectFit: 'contain' }}
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div className="sidebar-brand">
            <img src={payrollnootIcon} alt="" className="sidebar-brand-icon" />
            <div className="sidebar-brand-text" aria-label={productName}>
              <span className="sidebar-brand-payroll">Payroll</span>
              <span className="sidebar-brand-noot">Noot</span>
            </div>
          </div>
        )}
      </div>
      <ul className="p-0">
        {visibleItems.map((item, index) => {
          const locked = needsCompany && !item.companyOptional;
          const isActive = pathname === item?.path || item?.otherPaths?.includes(pathname);
          return (
            <li
              key={index}
              className={`d-flex justify-content-between align-items-center ${isActive && !locked ? 'active-item' : ''}`}
              style={locked ? { opacity: 0.42 } : undefined}
              title={locked ? 'Add or select a client company first' : undefined}
            >
              <NavLink
                onClick={(e) => { e.preventDefault(); handleNav(item); }}
                className="d-flex align-items-center gap-3"
                style={locked ? { cursor: 'not-allowed' } : undefined}
              >
                <img src={item.icon} alt={item.name} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {item.name}
                  {item.showBadge && pendingCount > 0 && (
                    <span style={{ background: '#1B75F0', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '1px 7px', lineHeight: '18px', minWidth: 20, textAlign: 'center' }}>
                      {pendingCount}
                    </span>
                  )}
                  {item.showOffboardingBadge && offboardingOpenCount > 0 && (
                    <span style={{ background: '#b45309', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '1px 7px', lineHeight: '18px', minWidth: 20, textAlign: 'center' }}>
                      {offboardingOpenCount}
                    </span>
                  )}
                </span>
              </NavLink>
              <img src={item.active ? orangearrow : arrow} className="img-fluid arrow-images" alt="Arrow" />
            </li>
          );
        })}
      </ul>

      {lockModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="company-required-title"
          onClick={() => setLockModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%',
              padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
          >
            <h5 id="company-required-title" style={{ margin: '0 0 8px', fontWeight: 700, color: '#1a3a5c', fontSize: 17 }}>
              {linkedCount > 0 ? 'Select a client company' : 'Add a client company first'}
            </h5>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#6b7280', lineHeight: 1.45 }}>
              {linkedCount > 0
                ? 'Employees, payroll, and other company pages need an active client. Open My Companies and switch into one.'
                : 'Invite a client to onboard or add a company quickly. Firm tools like My Companies and Billing stay available.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {linkedCount > 0 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => goMyCompanies()}
                >
                  Go to My Companies
                </button>
              ) : canManagePortfolio ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => goMyCompanies('invite')}
                  >
                    Invite to onboard
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => goMyCompanies('add')}
                  >
                    Add company (quick)
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => goMyCompanies()}
                >
                  Go to My Companies
                </button>
              )}
              <button
                type="button"
                className="btn btn-link"
                style={{ color: '#6b7280' }}
                onClick={() => setLockModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
